import { productionPlans } from "@/api/productionPlan/useProductionPlanQuery";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { ProductionPlanViewForm } from "@/features/productionPlan/view";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { MantineReactCommonTable } from "@/features/ui/mantineTable";
import { trackingStatusColor } from "@/utils/workStatus";
import { Badge } from "@mantine/core";
import {
  ProductionPlansGet200ResponseRowsInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface ProductionPlanFormProps {
  purchaseOrderItemID: number | undefined;
}
export const ProductionPlanForm = (params: ProductionPlanFormProps) => {
  const { purchaseOrderItemID } = params;
  const { t } = useTranslation();
  const { openModal } = useModal();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  // 각 컬럼 sort
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  // 각 컬럼 filter
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  // 전체 검색 (단, serchFields에 필드를 직접 입력해줘야함.)
  const [globalFilter, setGlobalFilter] = useState("");

  // sort를 id 별 string으로 만들어줌
  const sortString = sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(",");

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  // filter를 id 별 like를 사용하여 단어가 포함되어있는지 확인
  const filterQuery: { [key: string]: { $iLike: string } } = columnFilters.reduce<{
    [key: string]: { $iLike: string };
  }>((acc, filter) => {
    acc[filter.id] = { $iLike: `%${filter.value}%` as string };
    return acc;
  }, {});
  const { data } = useQuery(
    productionPlans.get({
      query: {
        $and: [{ purchaseOrderItemId: purchaseOrderItemID }],
      },
      pageSize: pagination.pageSize,
      page: pagination.pageIndex,
      search: globalFilter,
      sort: sortString || undefined,
      populate: ["works", "equipment", "itemUnit", "purchaseOrderItem", "lastLotId"],
    }),
  );
  const rows: readonly ProductionPlansGet200ResponseRowsInner[] = data?.data.rows ?? [];

  const columns = useMemo<MRT_ColumnDef<ProductionPlansGet200ResponseRowsInner>[]>(
    () => [
      {
        accessorKey: "id",
        header: t("productionPlan.id"),
        Cell: ({ cell }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(
                  <ProductionPlanViewForm ProductionPlanId={cell.getValue<number>()} />,
                  null,
                  t("common.detail", { item: t("productionPlan.productionPlan") }),
                );
              }}
            >
              {cell.getValue<string>()}
            </LinkAnchor>
          );
        },
      },
      {
        accessorKey: "itemName",
        header: t("item.name"),
      },
      {
        accessorKey: "itemCode",
        header: t("item.code"),
        Cell: ({ cell }) => (
          <LinkAnchor
            onClick={() => {
              openModal(
                <ItemsDetailForm itemCode={cell.getValue<string>()} />,
                null,
                t("common.detail", { item: t("item.item") }),
              );
            }}
          >
            {cell.getValue<string>()}
          </LinkAnchor>
        ),
      },
      {
        accessorKey: "targetQuantity",
        header: t("inventory.quantity"),
      },
      {
        accessorFn: (row) => row.works?.[0]?.trackingStatus,
        header: t("work.status"),
        Cell: ({ cell }) => (
          <Badge
            variant="outline"
            color={
              trackingStatusColor[
                cell.getValue<ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum>()
              ]
            }
          >
            {t(cell.getValue<ProductionPlansGet200ResponseRowsInnerWorksInnerTrackingStatusEnum>())}
          </Badge>
        ),
      },
      {
        accessorKey: "scheduledAt",
        header: t("work.workDay"),
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue<string>());
          return dayjs(date).format("YYYY-MM-DD");
        },
      },
    ],
    [t],
  );

  return (
    <MantineReactCommonTable
      data={rows as ProductionPlansGet200ResponseRowsInner[]}
      columns={columns}
      totalCount={data?.data.total}
      totalPages={data?.data.totalPages}
      pagination={pagination} // pagination
      setPagination={setPagination}
      sorting={sorting} // 정렬
      setSorting={setSorting}
      columnFilters={columnFilters} // 컬럼별 검색
      setColumnFilters={setColumnFilters}
      globalFilter={globalFilter} // 전체 검색
      setGlobalFilter={setGlobalFilter}
      rowSelection={rowSelection} // 선택
      setRowSelection={setRowSelection}
      selectedRowIds={selectedRowIds} // 선택된 id
      setSelectedRowIds={setSelectedRowIds}
      enableTopToolbar={true}
    />
  );
};
